import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import './UserManagement.css';
import './UserGuide.css';

/** 역할 코드 → 화면 표기 (DepositTable/HierarchyView와 동일 기준) */
const ROLE_LABELS = {
  super: '슈퍼어드민',
  admin: '본사',
  dev: '개발',
  chief_major: '총대본',
  representative: '대표자',
  major_branch: '대본사',
  branch: '부본사',
  jisa: '지사',
  distributor: '총판',
  agency: '매장'
};

const getRoleBadgeClass = (role) => {
  switch (role) {
    case 'super': return 'role-badge super';
    case 'chief_major': return 'role-badge chief-major';
    case 'major_branch': return 'role-badge major-branch';
    default: return `role-badge ${role || ''}`;
  }
};

const RoleBadge = ({ role }) => (
  <span className={getRoleBadgeClass(role)}>{ROLE_LABELS[role] || role}</span>
);

/** 사이드바 메뉴별 접근 권한 (App.js의 라우팅 조건과 동일 기준) */
const MENU_ACCESS = [
  {
    name: '정산 입력',
    adminOnly: true,
    adminScope: '하루치 실적과 역할별 건당 수수료를 분류별로 등록 · 수정',
    userScope: '-'
  },
  {
    name: '일별정산',
    adminScope: '모든 분류의 날짜별 정산 결과 확인, 정산 상태 변경',
    userScope: '본인과 하위 조직에 연결된 분류의 정산 결과 조회 (상태 변경 불가)'
  },
  {
    name: '사용자 계보도',
    adminScope: '전체 조직 트리와 사용자별 정산 대시보드',
    userScope: '본인과 하위 조직의 트리 · 정산 대시보드 조회'
  },
  {
    name: '사용자 및 요율 관리',
    adminOnly: true,
    adminScope: '계정 생성 · 수정 · 삭제, 분류별 요율 관리',
    userScope: '-'
  },
  {
    name: '분류 관리',
    adminOnly: true,
    adminScope: '정산 단위(분류) 생성 · 수정 · 삭제',
    userScope: '-'
  },
  {
    name: '월 이용료 정산',
    adminOnly: true,
    adminScope: '분류별 월 이용료 수납 내역 관리',
    userScope: '-'
  },
  {
    name: '유보금 입/출금 내역',
    adminOnly: true,
    adminScope: '분류별 유보금 입 · 출금 기록과 순액 확인',
    userScope: '-'
  },
  {
    name: 'FAQ 관리',
    adminOnly: true,
    adminScope: '자주 묻는 질문 등록 · 수정 · 답변 복사',
    userScope: '-'
  },
  {
    name: '업무 메모',
    adminOnly: true,
    adminScope: '내부 공유용 업무 메모 작성 · 수정',
    userScope: '-'
  },
  {
    name: '이용 가이드',
    adminScope: '전체 메뉴 사용법 열람',
    userScope: '권한이 있는 메뉴의 사용법 열람'
  }
];

const Callout = ({ type = 'info', title, children }) => (
  <div className={`guide-callout guide-callout--${type}`}>
    {title && <strong className="guide-callout-title">{title}</strong>}
    <div className="guide-callout-body">{children}</div>
  </div>
);

function UserGuide() {
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (e) {
      return null;
    }
  }, []);

  const isAdmin = ['super', 'admin'].includes(currentUser?.role);
  const myRoleLabel = ROLE_LABELS[currentUser?.role] || '내';
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(null);
  const tocRef = useRef(null);

  const sections = [
    {
      id: 'start',
      title: '시작하기 · 화면 구성',
      keywords: '로그인 로그아웃 사이드바 메뉴 권한 세션 토큰 비밀번호',
      body: (
        <>
          <h3 className="guide-h3">로그인</h3>
          <ol className="guide-steps">
            <li>브라우저에서 시스템 주소로 접속하면 <b>관리자 로그인</b> 화면이 나타납니다.</li>
            <li><b>아이디</b>와 <b>비밀번호</b>를 입력하고 <b>로그인</b> 버튼을 누릅니다.</li>
            <li>로그인에 성공하면 역할에 따라 첫 화면이 결정됩니다.
              <ul>
                <li>본사 계정 → <b>정산 입력</b> 화면</li>
                <li>그 외 역할 → <b>일별정산</b> 화면</li>
              </ul>
            </li>
          </ol>
          <Callout type="warn" title="로그인이 안 될 때">
            아이디 · 비밀번호를 다시 확인하고, 그래도 실패하면 <b>본사 담당자</b>에게 <b>비밀번호 재설정</b>을 요청하세요.
            <b>사용자 및 요율 관리</b> 메뉴는 본사(슈퍼어드민 포함) 전용이므로, 대본사 · 부본사 등 중간 상위 계정은 비밀번호를 바꿔줄 수 없습니다.
            재설정은 <b>사용자 및 요율 관리 → 수정</b>에서 본사가 진행합니다(최소 6자).
          </Callout>

          <h3 className="guide-h3">화면 구성</h3>
          <table className="guide-table">
            <thead>
              <tr><th style={{ width: '22%' }}>영역</th><th>설명</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>왼쪽 사이드바</td>
                <td>상단에 <b>사용자명</b>과 <b>로그아웃</b> 버튼, 그 아래에 메뉴가 있습니다. 현재 보고 있는 메뉴는 배경색으로 강조됩니다.</td>
              </tr>
              <tr>
                <td>본문 영역</td>
                <td>선택한 메뉴의 내용이 표시됩니다. 목록은 대부분 <b>카드 + 표</b> 형태이며, 하단에 페이지 이동 버튼이 있습니다.</td>
              </tr>
              <tr>
                <td>모달 창</td>
                <td>생성 · 수정은 팝업(모달)에서 진행합니다. <b>취소</b> 또는 오른쪽 위 <b>×</b>로 닫으면 입력 내용은 저장되지 않습니다.</td>
              </tr>
            </tbody>
          </table>

          <h3 className="guide-h3">{myRoleLabel} 계정이 사용할 수 있는 메뉴</h3>
          <p className="guide-p">
            사이드바에는 현재 로그인한 역할이 쓸 수 있는 메뉴만 표시됩니다.
            아래는 <b>{myRoleLabel}</b> 계정 기준 목록입니다.
          </p>
          <table className="guide-table">
            <thead>
              <tr><th style={{ width: '30%' }}>메뉴</th><th>사용 범위</th></tr>
            </thead>
            <tbody>
              {MENU_ACCESS.filter((m) => isAdmin || !m.adminOnly).map((m) => (
                <tr key={m.name}>
                  <td>{m.name}</td>
                  <td>{isAdmin ? m.adminScope : m.userScope}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isAdmin && (
            <Callout type="info" title="다른 메뉴는 왜 안 보이나요?">
              정산 입력 · 사용자 및 요율 관리 · 분류 관리 · 월 이용료 정산 · 유보금 입/출금 내역 · FAQ 관리 · 업무 메모는
              <b> 본사 전용</b> 메뉴입니다(FAQ 관리와 업무 메모는 서로 다른 메뉴입니다).
              해당 작업이 필요하면 본사 담당자에게 요청하세요.
            </Callout>
          )}
          <Callout type="info" title="로그아웃">
            사이드바 상단의 <b>로그아웃</b>을 누르면 저장된 로그인 정보가 모두 지워지고 로그인 화면으로 돌아갑니다.
            공용 PC에서는 작업 후 반드시 로그아웃하세요.
          </Callout>
        </>
      )
    },
    {
      id: 'roles',
      title: '역할 체계와 조직 트리',
      keywords: '역할 권한 계층 상위 하위 본사 대본사 부본사 지사 총판 매장 개발 총대본 대표자',
      body: (
        <>
          <p className="guide-p">
            모든 계정은 하나의 <b>역할</b>을 가지며, <b>상위 사용자</b>를 통해 트리(계보) 구조로 연결됩니다.
            자기 자신과 <b>하위 조직</b>의 데이터만 조회할 수 있는 것이 기본 원칙입니다.
          </p>
          <table className="guide-table">
            <thead>
              <tr>
                <th style={{ width: '18%' }}>역할</th>
                <th style={{ width: '16%' }}>계층</th>
                <th>설명</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><RoleBadge role="super" /></td><td>시스템 최상위</td><td>시스템 제공처가 쓰는 계정입니다. 본사 · 개발 · 총대본 · 대표자 계정을 만들 수 있는 유일한 역할이며, 화면 곳곳에 <b>슈퍼어드민</b>으로 표시됩니다. 그 외 화면 구성과 권한은 본사와 같습니다.</td></tr>
              <tr><td><RoleBadge role="admin" /></td><td>운영 최상위</td><td>플랫폼 운영을 총괄하는 관리자 계정입니다. 정산 입력, 분류 · 요율 · 월 이용료 · 유보금 관리와 조직(대본사~매장) 계정 생성을 담당합니다.</td></tr>
              <tr><td><RoleBadge role="dev" /></td><td>별도</td><td>조직 트리와 별개로 운영되는 역할입니다. 정산 계산 시 <b>본사 건당 수수료</b>를 사용합니다.</td></tr>
              <tr><td><RoleBadge role="chief_major" /></td><td>별도</td><td>조직 트리와 별개 역할. 건당 수수료는 <b>대본사</b> 값을 따릅니다.</td></tr>
              <tr><td><RoleBadge role="representative" /></td><td>별도</td><td>조직 트리와 별개 역할. 건당 수수료는 <b>부본사</b> 값을 따릅니다.</td></tr>
              <tr><td><RoleBadge role="major_branch" /></td><td>2단계</td><td>본사 아래 조직 계층의 시작입니다.</td></tr>
              <tr><td><RoleBadge role="branch" /></td><td>3단계</td><td>대본사 아래 계층입니다.</td></tr>
              <tr><td><RoleBadge role="jisa" /></td><td>4단계</td><td>부본사 아래 계층입니다.</td></tr>
              <tr><td><RoleBadge role="distributor" /></td><td>5단계</td><td>지사 아래 계층입니다.</td></tr>
              <tr><td><RoleBadge role="agency" /></td><td>6단계(최하위)</td><td>실제 매장 단위입니다. 하위 계정을 만들 수 없습니다.</td></tr>
            </tbody>
          </table>

          <h3 className="guide-h3">상위 → 하위 생성 규칙</h3>
          <table className="guide-table">
            <thead>
              <tr><th style={{ width: '30%' }}>선택한 상위 사용자</th><th>만들 수 있는 역할</th></tr>
            </thead>
            <tbody>
              <tr><td>본사</td><td>대본사 · 부본사 · 지사 · 총판 · 매장</td></tr>
              <tr><td>대본사</td><td>부본사 · 지사 · 총판 · 매장</td></tr>
              <tr><td>부본사</td><td>지사 · 총판 · 매장</td></tr>
              <tr><td>지사</td><td>총판 · 매장</td></tr>
              <tr><td>총판</td><td>매장</td></tr>
              <tr><td>매장</td><td>없음</td></tr>
            </tbody>
          </table>
          <Callout type="tip" title="한 단계 건너뛰기도 가능합니다">
            예를 들어 본사 아래에 대본사를 거치지 않고 바로 <b>지사</b>를 만들 수 있습니다.
            다만 계층 순서를 거스르는 조합(예: 매장 아래 지사)은 선택 목록에 나타나지 않습니다.
          </Callout>
          <Callout type="warn" title="개발 · 총대본 · 대표자">
            이 세 역할은 조직 트리와 <b>별도</b>로 운영되며 상위 사용자를 두지 않습니다.
            <b>슈퍼어드민만 생성</b>할 수 있으므로 본사 화면에서는 새로 만들 수 없고, 필요하면 시스템 제공처에 요청해 발급받습니다.
            이미 발급된 계정은 <b>분류별 요율</b>에 일반 역할처럼 등록해 정산할 수 있습니다.
          </Callout>
        </>
      )
    },
    {
      id: 'flow',
      title: '전체 업무 흐름 (처음 세팅 → 매일 운영)',
      keywords: '흐름 순서 절차 세팅 초기 운영 체크리스트 일일',
      body: (
        <>
          <h3 className="guide-h3">처음 한 번만 하는 세팅</h3>
          <ol className="guide-steps guide-steps--big">
            <li><b>분류 만들기</b> — <i>분류 관리</i>에서 정산 단위(업체 · 사이트 등)를 등록합니다. 모든 데이터가 이 분류를 기준으로 묶입니다.</li>
            <li><b>계정 만들기</b> — <i>사용자 및 요율 관리</i>에서 상위 → 하위 순서로 계정을 생성합니다.</li>
            <li><b>요율 등록</b> — 같은 화면 아래 <i>분류별 요율 관리</i>에서 분류마다 <b>역할 + 사용자 + 요율(%)</b>을 등록합니다.</li>
          </ol>
          <Callout type="info" title="새 분류를 만들면 요율 4줄이 미리 만들어집니다">
            분류를 생성하면 <b>본사 · 개발 · 총대본 · 대표자</b> 요율이 <b>0%</b>로 자동 등록됩니다(해당 역할 계정이 있는 경우).
            비어 있는 게 아니라 <b>0%로 채워진 상태</b>이므로, 실제 배분율에 맞게 <b>수정</b>해 주세요. 0%로 두면 그 역할의 요율 정산액은 0원이 됩니다.
          </Callout>
          <Callout type="warn" title="요율을 등록하지 않으면 정산액이 계산되지 않습니다">
            일별정산 표와 계보도 대시보드는 모두 <b>분류별 요율</b>을 기준으로 계산합니다.
            요율이 없는 역할 · 사용자는 표에 나타나지 않거나 금액이 0으로 표시됩니다.
          </Callout>

          <h3 className="guide-h3">매일 반복하는 운영</h3>
          <ol className="guide-steps guide-steps--big">
            <li><b>정산 입력</b> — 전날 실적(총 입금액 · 총 입금건수 · 총 출금건수)과 역할별 건당 수수료를 분류별로 입력합니다.</li>
            <li><b>일별정산 확인</b> — 날짜를 선택해 역할별 정산액이 맞는지 확인합니다.</li>
            <li><b>지급 후 상태 변경</b> — 실제 지급이 끝난 행의 <b>정산 상태</b> 토글을 켜서 <b>정산 완료</b>로 바꿉니다.</li>
            <li><b>계보도에서 점검</b> — <i>사용자 계보도</i>에서 사용자별 미정산 금액과 기간 합계를 확인합니다.</li>
            <li><b>부가 내역 기록</b> — 월 이용료 수납, 유보금 입 · 출금이 발생하면 해당 메뉴에 기록합니다.</li>
          </ol>
        </>
      )
    },
    {
      id: 'settlement-input',
      title: '정산 입력',
      adminOnly: true,
      keywords: '정산 입력 총 입금액 입금건수 출금건수 건당 수수료 440 990 수정 삭제 중복',
      body: (
        <>
          <p className="guide-p">
            하루치 실적을 <b>분류 단위</b>로 입력하는 화면입니다. 왼쪽은 <b>신규 정산 입력</b> 폼, 오른쪽은 <b>입력 내역</b> 목록입니다.
            여기에 입력한 값이 일별정산 · 계보도 대시보드 계산의 원본 데이터가 됩니다.
          </p>

          <h3 className="guide-h3">입력 항목</h3>
          <table className="guide-table">
            <thead>
              <tr><th style={{ width: '24%' }}>항목</th><th style={{ width: '14%' }}>기본값</th><th>설명</th></tr>
            </thead>
            <tbody>
              <tr><td>정산 날짜</td><td>어제 날짜</td><td>실적이 발생한 날짜입니다. 보통 전날 실적을 다음 날 입력합니다.</td></tr>
              <tr><td>분류</td><td>-</td><td><i>분류 관리</i>에 등록된 목록에서 선택합니다. 반드시 선택해야 저장됩니다.</td></tr>
              <tr><td>총 입금액</td><td>-</td><td>해당 날짜 · 분류의 총 입금 금액(원). 요율 정산액 계산의 기준이 됩니다.</td></tr>
              <tr><td>총 입금건수</td><td>-</td><td>입금 건수. <b>입금 건당 수수료</b>와 곱해집니다.</td></tr>
              <tr><td>총 출금건수</td><td>-</td><td>출금 건수. <b>출금 건당 수수료</b>와 곱해집니다.</td></tr>
              <tr>
                <td>입출금 건당 수수료</td>
                <td>본사 440 / 990<br />나머지 0 / 0</td>
                <td>
                  본사 · 대본사 · 부본사 · 지사 · 총판 · 매장 <b>6개 역할</b>에 대해 각각
                  <b> 왼쪽 = 입금 건당</b>, <b>오른쪽 = 출금 건당</b> 금액을 원 단위로 입력합니다.
                </td>
              </tr>
            </tbody>
          </table>
          <Callout type="tip" title="입력 요령">
            <ul className="guide-ul">
              <li>금액 · 건수는 <b>0 이상의 숫자</b>만 저장됩니다. 음수나 문자를 넣으면 오류 메시지가 표시됩니다.</li>
              <li>숫자 칸 위에서 마우스 휠을 굴려도 값이 바뀌지 않도록 되어 있으니 안심하고 스크롤하세요.</li>
              <li>건당 수수료는 매일 같은 값을 쓰는 경우가 많습니다. 값이 바뀐 역할만 수정하면 됩니다.</li>
            </ul>
          </Callout>
          <Callout type="warn" title="같은 날짜 + 같은 분류는 한 번만">
            이미 입력된 날짜 · 분류를 다시 저장하려고 하면
            <b>「해당 날짜와 분류에 이미 입력된 내역이 있습니다」</b> 오류가 표시됩니다.
            오른쪽 목록에서 해당 행의 <b>수정</b>을 이용하세요.
          </Callout>

          <h3 className="guide-h3">입력 내역 관리</h3>
          <ol className="guide-steps">
            <li>오른쪽 위 <b>월 선택</b>으로 조회할 달을 바꿉니다. 목록은 <b>정산일 그룹</b>으로 묶여 최신 날짜부터 표시됩니다.</li>
            <li>한 페이지에 20건씩 표시되며, 아래 <b>이전 / 다음</b> 버튼으로 이동합니다.</li>
            <li><b>수정</b>을 누르면 그 행이 바로 편집 상태로 바뀝니다. 금액 · 건수 · 역할별 수수료를 고친 뒤 <b>저장</b>을 누릅니다.</li>
            <li><b>삭제</b>는 확인 창이 뜬 뒤 실행되며, 되돌릴 수 없습니다.</li>
          </ol>
        </>
      )
    },
    {
      id: 'daily',
      title: '일별정산 (계산 규칙 포함)',
      keywords: '일별정산 계산식 요율 정산액 건당 수수료 합계 정산 상태 토글 미정산',
      body: (
        <>
          <p className="guide-p">
            선택한 날짜의 정산 결과를 <b>분류별 카드</b>로 보여줍니다. 카드 위쪽에는 그날의 입력 요약(총 입금액 · 총 입금건수 · 총 출금건수),
            아래쪽에는 <b>역할별 정산 표</b>가 나옵니다.
          </p>
          <Callout type="info" title="처음 열면 어떤 날짜가 보이나요?">
            화면을 열면 <b>가장 최근에 정산 입력이 있는 날짜</b>가 자동으로 선택됩니다. 오른쪽 위 <b>정산 날짜</b>에서 원하는 날로 바꿀 수 있습니다.
          </Callout>

          <h3 className="guide-h3">표의 각 열이 계산되는 방식</h3>
          <table className="guide-table">
            <thead>
              <tr><th style={{ width: '26%' }}>열</th><th>계산식</th></tr>
            </thead>
            <tbody>
              <tr><td>요율</td><td>분류별 요율 관리에 등록된 값(%)</td></tr>
              <tr><td>입금 요율 정산액</td><td className="guide-formula">총 입금액 × 요율 ÷ 100</td></tr>
              <tr><td>입금건수 수수료</td><td className="guide-formula">총 입금건수 × 해당 역할의 입금 건당 수수료</td></tr>
              <tr><td>출금건수 수수료</td><td className="guide-formula">총 출금건수 × 해당 역할의 출금 건당 수수료</td></tr>
              <tr><td><b>총 정산액</b></td><td className="guide-formula"><b>입금 요율 정산액 + 입금건수 수수료 + 출금건수 수수료</b></td></tr>
              <tr><td>합계 행</td><td>표에 표시된 모든 역할의 값을 세로로 더한 값입니다. 요율 열은 요율의 <b>단순 합</b>입니다.</td></tr>
            </tbody>
          </table>

          <h3 className="guide-h3">역할별로 어떤 건당 수수료가 적용되나요?</h3>
          <table className="guide-table">
            <thead>
              <tr><th style={{ width: '40%' }}>정산 표의 역할</th><th>정산 입력에서 사용하는 수수료 칸</th></tr>
            </thead>
            <tbody>
              <tr><td><RoleBadge role="admin" /> <RoleBadge role="dev" /></td><td>본사 (기본 입금 440 / 출금 990)</td></tr>
              <tr><td><RoleBadge role="chief_major" /> <RoleBadge role="major_branch" /></td><td>대본사</td></tr>
              <tr><td><RoleBadge role="representative" /> <RoleBadge role="branch" /></td><td>부본사</td></tr>
              <tr><td><RoleBadge role="jisa" /></td><td>지사</td></tr>
              <tr><td><RoleBadge role="distributor" /></td><td>총판</td></tr>
              <tr><td><RoleBadge role="agency" /></td><td>매장</td></tr>
            </tbody>
          </table>

          <h3 className="guide-h3">계산 예시</h3>
          <div className="guide-example">
            <p className="guide-example-head">
              총 입금액 <b>10,000,000원</b> · 총 입금건수 <b>100건</b> · 총 출금건수 <b>20건</b>,
              매장 요율 <b>1.5%</b>, 매장 건당 수수료 <b>입금 200원 / 출금 500원</b>인 경우
            </p>
            <table className="guide-table">
              <tbody>
                <tr><td style={{ width: '40%' }}>입금 요율 정산액</td><td>10,000,000 × 1.5 ÷ 100 = <b>150,000원</b></td></tr>
                <tr><td>입금건수 수수료</td><td>100 × 200 = <b>20,000원</b></td></tr>
                <tr><td>출금건수 수수료</td><td>20 × 500 = <b>10,000원</b></td></tr>
                <tr><td><b>총 정산액</b></td><td><b>180,000원</b></td></tr>
              </tbody>
            </table>
          </div>

          <h3 className="guide-h3">정산 상태 (정산 완료 / 미정산)</h3>
          <ul className="guide-ul">
            <li>각 행의 오른쪽 토글로 <b>정산 완료</b> / <b>미정산</b>을 표시합니다.</li>
            <li>토글을 켜고 끌 수 있는 것은 <b>본사 계정</b>뿐입니다. 다른 역할에는 상태 글자만 보입니다.</li>
            <li>상태는 <b>날짜 + 요율(역할)</b> 단위로 저장됩니다. 즉 같은 사용자라도 날짜마다 따로 관리됩니다.</li>
            <li>여기서 켠 상태가 계보도 대시보드의 <b>정산 완료 금액 / 미정산 금액</b>에 그대로 반영됩니다.</li>
          </ul>
          <Callout type="warn" title="화면이 비어 있다면">
            <b>「선택한 날짜의 정산 입력이 없습니다」</b>가 보이면 그 날짜에 정산 입력이 없는 것입니다.
            <b>「요율 정보가 없습니다」</b>가 보이면 해당 분류에 등록된 요율이 없는 것이니 <i>사용자 및 요율 관리</i>에서 요율을 먼저 등록하세요.
          </Callout>
        </>
      )
    },
    {
      id: 'hierarchy',
      title: '사용자 계보도 · 정산 대시보드',
      keywords: '계보도 트리 검색 필터 펼치기 접기 대시보드 기간 일일 일주일 한달 미정산 일 평균',
      body: (
        <>
          <p className="guide-p">
            왼쪽은 조직 <b>트리</b>, 오른쪽은 선택한 사용자의 <b>상세 정보 + 정산 대시보드</b>입니다.
            각 계정은 <b>자기 자신과 하위 조직</b>만 볼 수 있으므로, 본사 계정에서는 전체 조직이 표시됩니다.
          </p>

          <h3 className="guide-h3">트리 사용법</h3>
          <ul className="guide-ul">
            <li><b>검색</b> — 사용자명 · 아이디 · 코드(<span className="guide-code">#001</span> 형식)로 찾습니다. 하위에 일치하는 사용자가 있으면 상위 줄도 함께 표시됩니다(단, 하위까지 훑을 때는 <b>사용자명과 코드</b>만 대조하므로 아이디로 찾을 때는 트리를 펼쳐 두는 편이 확실합니다).</li>
            <li><b>역할 필터</b> — 특정 역할만 남겨서 봅니다. 해당 역할을 하위에 가진 상위 줄은 경로 확인을 위해 함께 표시됩니다.</li>
            <li><b>▶ / ▼</b> — 줄 왼쪽의 화살표로 하위를 펼치거나 접습니다. <b>전체 펼치기 / 전체 접기</b> 버튼도 있습니다.</li>
            <li>줄을 <b>클릭</b>하면 오른쪽에 그 사용자의 상세 정보와 대시보드가 표시됩니다.</li>
            <li>줄 오른쪽의 <b>하위 N개</b>는 바로 아래 직계 하위 수입니다.</li>
          </ul>

          <h3 className="guide-h3">정산 대시보드</h3>
          <ol className="guide-steps">
            <li><b>기간 선택</b> — 날짜를 직접 지정하거나 <b>일일</b>(어제) · <b>일주일</b>(오늘 포함 최근 7일) · <b>한달</b>(오늘 포함 최근 30일) 버튼을 씁니다.</li>
            <li><b>분류 탭</b> — <b>종합</b>은 연결된 모든 분류의 합계, 분류명 탭은 그 분류만 봅니다.</li>
            <li><b>🔄</b> 버튼으로 현재 조건 그대로 데이터를 다시 불러옵니다.</li>
          </ol>
          <table className="guide-table">
            <thead>
              <tr><th style={{ width: '26%' }}>지표</th><th>의미</th></tr>
            </thead>
            <tbody>
              <tr><td>총 입금액</td><td>기간 내 해당 분류들의 총 입금액 합계</td></tr>
              <tr><td>총 정산액</td><td>정산 완료 금액 + 미정산 금액</td></tr>
              <tr><td>정산 완료 금액</td><td>일별정산에서 <b>정산 완료</b>로 표시된 날짜의 정산액 합계</td></tr>
              <tr><td>미정산 금액</td><td>아직 정산 완료로 표시되지 않은 정산액 합계</td></tr>
              <tr><td>월 이용료</td><td>기간 내 <i>월 이용료 정산</i>에 등록된 수납 금액 합계</td></tr>
              <tr><td>일 평균 금액</td><td>총 정산액 ÷ 기간 일수 (소수점 버림)</td></tr>
            </tbody>
          </table>
          <ul className="guide-ul">
            <li><b>분류별 요율 · 수수료</b> 영역의 ON/OFF 토글로 요율 · 건당 수수료 카드를 감출 수 있습니다. 표시되는 건당 수수료는 그 분류의 <b>가장 최근 정산 입력</b> 값 기준입니다.</li>
            <li><b>기간 내 미정산 내역</b>은 정산일 · 분류 · 상태 · 미정산 금액을 한 페이지에 12건씩 보여줍니다(미정산 건만 모아 보여주므로 상태는 항상 <b>미정산</b>입니다).</li>
          </ul>
          <Callout type="warn" title="「연결된 정산 분류가 없습니다」가 보일 때">
            대시보드는 <b>분류별 요율</b>의 <b>사용자명</b>이 그 계정과 일치할 때만 데이터를 연결합니다.
            <i>사용자 및 요율 관리 → 분류별 요율 관리</i>에서 해당 분류에 그 사용자의 요율이 등록되어 있는지 확인하세요.
          </Callout>
          <Callout type="info" title="같은 날짜 · 같은 분류가 중복 입력된 경우">
            대시보드 집계에서는 <b>가장 나중에 입력된 한 건</b>만 사용합니다. 중복 입력이 의심되면 <i>정산 입력</i> 목록에서 정리하세요.
          </Callout>
        </>
      )
    },
    {
      id: 'users',
      title: '사용자 및 요율 관리',
      adminOnly: true,
      keywords: '사용자 생성 수정 삭제 비밀번호 계좌 상위 요율 퍼센트 초기화',
      body: (
        <>
          <h3 className="guide-h3">사용자 만들기</h3>
          <ol className="guide-steps">
            <li>오른쪽 위 <b>+ 새 사용자 생성</b>을 누릅니다.</li>
            <li><b>아이디</b>(로그인용)와 <b>비밀번호</b>(최소 6자)를 입력합니다.</li>
            <li><b>사용자명</b>은 화면 곳곳에 표시되는 이름입니다. <b>요율 등록 시 이 이름으로 연결</b>되므로 알아보기 쉽게 지정하세요.</li>
            <li><b>계좌정보</b>는 선택 항목입니다(최대 100자).</li>
            <li><b>상위 사용자</b>를 먼저 고릅니다. 그 선택에 따라 <b>역할</b> 목록이 자동으로 좁혀집니다.</li>
            <li><b>생성</b>을 누르면 목록이 갱신됩니다.</li>
          </ol>

          <h3 className="guide-h3">수정 · 삭제</h3>
          <ul className="guide-ul">
            <li><b>수정</b>에서는 아이디 · 사용자명 · 계좌정보 · 상위 사용자 · 비밀번호를 바꿀 수 있습니다. <b>역할은 변경할 수 없습니다</b>(읽기 전용).</li>
            <li>비밀번호 칸을 <b>비워 두면 기존 비밀번호가 유지</b>됩니다. 바꿀 때만 6자 이상으로 입력하세요.</li>
            <li>본사 계정으로는 <b>자기 자신</b>과 <b>다른 본사 · 개발 계정</b>을 수정할 수 없습니다(수정 버튼이 비활성). 조직(대본사~매장)과 총대본 · 대표자 계정을 수정합니다.</li>
            <li><b>삭제</b>는 범위가 조금 다릅니다. 본사는 <b>개발 · 총대본 · 대표자 · 대본사~매장</b>을 삭제할 수 있습니다.
              즉 개발 계정은 <b>수정은 못 하지만 삭제는 가능</b>하니 실수하지 않도록 주의하세요.</li>
          </ul>
          <Callout type="warn" title="삭제는 되돌릴 수 없습니다">
            삭제되는 것은 <b>계정(로그인 정보)뿐</b>입니다. 이미 입력된 정산 데이터와
            <b> 분류별 요율에 등록된 그 사용자명은 그대로 남습니다.</b>
            요율이 남아 있으면 계보도에서 실체 없는 이름으로 계속 집계되므로,
            계정을 지웠다면 <i>분류별 요율 관리</i>에서 해당 요율도 함께 정리하세요.
            사용을 잠시 멈추는 용도라면 삭제 대신 비밀번호 변경 등 다른 방법을 검토하세요.
          </Callout>

          <h3 className="guide-h3">분류별 요율 관리</h3>
          <ol className="guide-steps">
            <li>화면 아래쪽에 <b>분류마다 카드</b>가 하나씩 표시됩니다. 카드 제목을 누르면 접기 · 펼치기가 되고, 그 상태는 새로고침 후에도 유지됩니다.</li>
            <li>카드 제목 옆 <b>요율 합계</b>는 그 분류에 등록된 모든 역할 요율의 합입니다.</li>
            <li><b>+ 역할 추가</b>를 눌러 <b>역할 · 사용자명 · 요율(%)</b>을 등록합니다. 요율은 0 ~ 100 사이여야 합니다.</li>
            <li>본사 · 개발은 사용자명이 자동으로 채워지고, 나머지 역할은 <b>등록된 사용자 중에서 선택</b>합니다.</li>
            <li>한 분류에 <b>본사 · 개발 · 총대본 · 대표자 · 대본사 · 부본사</b>는 각각 <b>1개만</b> 등록할 수 있습니다(이미 있으면 역할 선택 목록에서 비활성화).</li>
            <li><b>지사 · 총판 · 매장</b>은 <b>사용자별로 여러 개</b> 등록할 수 있습니다. 다만 <b>같은 사용자를 같은 분류에 두 번</b> 등록할 수는 없어,
              이미 등록된 사용자는 목록에 <b>(등록됨)</b>으로 표시되며 선택되지 않습니다.</li>
          </ol>
          <Callout type="tip" title="요율 합계 확인">
            요율 합계가 의도한 배분율(예: 100%)을 넘거나 모자라지 않는지 카드 제목 옆 숫자로 항상 확인하세요.
            시스템이 자동으로 막지는 않습니다.
          </Callout>
          <Callout type="warn" title="「일별정산 초기화」 버튼">
            분류 카드의 <b>일별정산 초기화</b>는 그 분류의 <b>정산 입력 내역 전부</b>와
            <b> 그 분류 요율에 매겨진 정산 완료 / 미정산 상태까지</b> 함께 삭제합니다.
            요율 · 사용자 계정은 남지만 입력 데이터와 정산 상태는 복구할 수 없으니, 테스트 데이터를 정리할 때만 사용하세요.
          </Callout>
        </>
      )
    },
    {
      id: 'companies',
      title: '분류 관리',
      adminOnly: true,
      keywords: '분류 업체 생성 수정 삭제 company',
      body: (
        <>
          <p className="guide-p">
            <b>분류</b>는 이 시스템의 가장 기본이 되는 단위입니다. 정산 입력 · 요율 · 월 이용료 · 유보금이 모두 분류를 기준으로 묶입니다.
          </p>
          <ul className="guide-ul">
            <li><b>+ 새 분류 추가</b> — 분류명을 입력해 등록합니다. 같은 이름은 중복 등록할 수 없습니다.</li>
            <li><b>수정</b> — 분류명을 변경합니다.</li>
            <li><b>삭제</b> — 확인 창에서 승인하면 삭제됩니다.</li>
          </ul>
          <Callout type="warn" title="분류를 삭제하면 함께 사라지는 데이터">
            해당 분류의 <b>일별정산 입력 · 분류별 요율 · 월 이용료 · 유보금</b> 데이터가 함께 삭제됩니다.
            (사용자 계정은 삭제되지 않습니다.) 이름만 바꾸면 되는 상황이라면 <b>수정</b>을 사용하세요.
          </Callout>
        </>
      )
    },
    {
      id: 'monthly-fee',
      title: '월 이용료 정산',
      adminOnly: true,
      keywords: '월 이용료 수납 금액 메모 월별',
      body: (
        <>
          <p className="guide-p">
            분류별로 받은 <b>월 이용료 수납 내역</b>을 기록합니다. 여기에 등록한 금액이 계보도 대시보드의 <b>월 이용료</b> 지표에 합산됩니다.
          </p>
          <ol className="guide-steps">
            <li>왼쪽 폼에서 <b>수납 날짜</b>(기본값: 오늘) · <b>분류</b> · <b>수납 금액</b>을 입력합니다. <b>메모</b>는 선택입니다.</li>
            <li><b>월 이용료 저장</b>을 누르면 오른쪽 목록에 반영됩니다.</li>
            <li>오른쪽 위 <b>월 선택</b>으로 조회할 달을 바꿉니다. 한 페이지에 20건씩 표시됩니다.</li>
            <li>목록의 <b>수정</b>을 누르면 수납일 · 분류 · 금액 · 메모를 그 자리에서 고칠 수 있습니다.</li>
          </ol>
          <Callout type="info" title="집계 기준">
            대시보드의 월 이용료는 <b>수납 날짜</b>가 선택한 기간에 포함되는 내역을 합산합니다. 실제 입금일에 맞춰 날짜를 지정하세요.
          </Callout>
        </>
      )
    },
    {
      id: 'reserve',
      title: '유보금 입/출금 내역',
      adminOnly: true,
      keywords: '유보금 입금 출금 순액 필터 등록',
      body: (
        <>
          <p className="guide-p">
            분류별 <b>유보금</b>의 입금 · 출금을 기록하고 잔액(순액)을 확인하는 화면입니다.
          </p>
          <ol className="guide-steps">
            <li>오른쪽 위 <b>유보금 등록</b> 버튼을 누르면 입력 창이 열립니다.</li>
            <li><b>일자</b>(기본값: 오늘) · <b>분류</b> · <b>구분</b>(입금 / 출금) · <b>금액</b>을 입력하고 <b>유보금 저장</b>을 누릅니다. <b>메모</b>는 선택입니다.</li>
            <li>목록은 <b>분류별로 그룹</b>지어 표시되고, 각 그룹 맨 아래 <b>총계</b> 줄에 입금 합계 · 출금 합계 · <b>순액(입금 − 출금)</b>과 건수가 나옵니다.</li>
            <li>상단 필터로 <b>분류</b>와 <b>입금 / 출금</b>을 좁혀 볼 수 있습니다. 한 페이지에 20건씩 표시됩니다.</li>
          </ol>
          <Callout type="warn" title="그룹 총계는 「지금 보고 있는 페이지」 기준입니다">
            분류 그룹 아래 총계는 <b>현재 페이지에 표시된 20건만</b> 더한 값입니다. 내역이 여러 페이지면 그 분류의 전체 잔액이 아닙니다.
            한 분류의 정확한 순액을 보려면 상단 <b>분류 필터</b>로 그 분류만 남기고 페이지별 값을 확인하세요.
          </Callout>
          <Callout type="info" title="수정 시 주의">
            목록에서 <b>수정</b>하면 일자 · 구분 · 금액 · 메모는 바꿀 수 있지만 <b>분류는 변경되지 않습니다</b>.
            분류를 잘못 지정했다면 삭제 후 다시 등록하세요.
          </Callout>
        </>
      )
    },
    {
      id: 'faq-memo',
      title: 'FAQ 관리 · 업무 메모',
      adminOnly: true,
      keywords: 'FAQ 질문 답변 복사 메모 업무 기록',
      body: (
        <>
          <p className="guide-p">
            사이드바에서는 <b>FAQ 관리</b>와 <b>업무 메모</b>가 <b>서로 다른 두 개의 메뉴</b>입니다. 성격이 비슷해 한 항목에 모아 설명합니다.
          </p>
          <h3 className="guide-h3">FAQ 관리</h3>
          <ul className="guide-ul">
            <li><b>+ FAQ 등록</b>으로 질문과 답변을 저장합니다. 둘 다 필수입니다.</li>
            <li>목록에서 질문 줄을 누르면 답변이 펼쳐집니다(+ / − 표시).</li>
            <li><b>답변 복사</b>를 누르면 답변 전체가 클립보드에 복사되어 <b>✓ 복사됨</b>으로 잠시 바뀝니다. 응대용 문구를 그대로 붙여 넣을 때 유용합니다.</li>
            <li>펼친 상태에서 <b>수정</b> · <b>삭제</b>할 수 있습니다.</li>
          </ul>

          <h3 className="guide-h3">업무 메모</h3>
          <ul className="guide-ul">
            <li><b>+ 메모 작성</b>으로 제목(선택)과 내용(필수)을 저장합니다. 줄바꿈은 그대로 유지됩니다.</li>
            <li>카드에 <b>최종 수정 시각</b>과 <b>작성자 아이디</b>가 표시되고, 최근에 수정한 메모가 위로 올라옵니다.</li>
            <li>본사 계정만 접근할 수 있는 내부 공유용 메모입니다.</li>
          </ul>
        </>
      )
    },
    {
      id: 'trouble',
      title: '자주 겪는 문제와 해결',
      keywords: '오류 문제 해결 권한 없음 데이터 없음 로그인 실패 트러블슈팅',
      body: (
        <table className="guide-table">
          <thead>
            <tr><th style={{ width: '38%' }}>증상</th><th>원인과 해결</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>「해당 날짜와 분류에 이미 입력된 내역이 있습니다」</td>
              <td>같은 날짜 · 분류로 이미 저장된 내역이 있습니다. 오른쪽 <b>입력 내역</b>에서 <b>수정</b>으로 값을 바꾸세요.</td>
            </tr>
            <tr>
              <td>「선택한 날짜의 정산 입력이 없습니다」</td>
              <td>그 날짜의 정산 입력이 없습니다. 날짜를 바꾸거나 <i>정산 입력</i>에서 먼저 등록하세요.</td>
            </tr>
            <tr>
              <td>「요율 정보가 없습니다」 / 금액이 0으로 나옴</td>
              <td>해당 분류에 요율이 없거나 요율이 0입니다. <i>사용자 및 요율 관리 → 분류별 요율 관리</i>에서 등록 · 수정하세요.</td>
            </tr>
            <tr>
              <td>계보도에서 「연결된 정산 분류가 없습니다」</td>
              <td>요율의 <b>사용자명</b>과 계정의 사용자명이 일치하지 않습니다. 요율을 그 사용자로 다시 등록하세요.</td>
            </tr>
            <tr>
              <td>「접근 권한이 없습니다」</td>
              <td>역할에 허용되지 않은 기능입니다. 필요한 작업이면 본사 담당자에게 요청하세요.</td>
            </tr>
            <tr>
              <td>메뉴가 보이지 않음</td>
              <td>정산 입력 · 사용자 · 분류 · 월 이용료 · 유보금 · FAQ · 메모는 <b>본사 전용</b> 메뉴입니다.</td>
            </tr>
            <tr>
              <td>정산 상태 토글이 없음</td>
              <td>상태 변경은 <b>본사 계정</b>만 가능합니다. 다른 역할에는 현재 상태만 표시됩니다.</td>
            </tr>
            <tr>
              <td>합계 금액이 예상과 다름</td>
              <td>① 요율 값, ② 역할별 건당 수수료, ③ 같은 날짜 · 분류 <b>중복 입력</b> 여부를 순서대로 확인하세요.</td>
            </tr>
            <tr>
              <td>사용자 생성 시 역할을 고를 수 없음</td>
              <td><b>상위 사용자를 먼저</b> 선택해야 역할 목록이 열립니다. 매장은 하위를 만들 수 없습니다.</td>
            </tr>
            <tr>
              <td>화면이 갱신되지 않음</td>
              <td>브라우저 새로고침(F5) 후 다시 확인하고, 계속되면 로그아웃 후 다시 로그인하세요.</td>
            </tr>
          </tbody>
        </table>
      )
    },
    {
      id: 'glossary',
      title: '용어 정리',
      keywords: '용어 사전 분류 요율 건당 수수료 유보금 월 이용료 미정산',
      body: (
        <table className="guide-table">
          <thead>
            <tr><th style={{ width: '24%' }}>용어</th><th>뜻</th></tr>
          </thead>
          <tbody>
            <tr><td>분류</td><td>정산의 기본 단위(업체 · 사이트 등). 모든 데이터가 이 이름으로 묶입니다.</td></tr>
            <tr><td>정산 입력</td><td>하루치 실적(총 입금액 · 입금건수 · 출금건수)과 역할별 건당 수수료를 등록한 원본 데이터.</td></tr>
            <tr><td>요율(%)</td><td>총 입금액에 곱해 정산액을 구하는 비율. 분류 · 역할 · 사용자 단위로 등록합니다.</td></tr>
            <tr><td>건당 수수료</td><td>입금 · 출금 <b>1건당</b> 지급하는 고정 금액. 역할마다 다르게 설정합니다.</td></tr>
            <tr><td>총 정산액</td><td>요율 정산액 + 입금건수 수수료 + 출금건수 수수료.</td></tr>
            <tr><td>정산 완료 / 미정산</td><td>실제 지급이 끝났는지 표시하는 상태. 날짜 + 역할(요율) 단위로 관리됩니다.</td></tr>
            <tr><td>월 이용료</td><td>분류별로 받는 월 단위 이용 요금 수납 내역.</td></tr>
            <tr><td>유보금</td><td>분류별로 보관 · 정산하는 금액의 입 · 출금 기록. 순액 = 입금 합계 − 출금 합계.</td></tr>
            <tr><td>계보도</td><td>상위 → 하위로 이어지는 사용자 조직 트리.</td></tr>
            <tr><td>코드(#001)</td><td>계보도에서 사용자를 구분하는 번호. 검색에 사용할 수 있습니다.</td></tr>
          </tbody>
        </table>
      )
    }
  ];

  /** 권한에 따라 보이는 섹션만 남기고, 그 순서대로 번호를 다시 매긴다 */
  const visibleSections = sections
    .filter((s) => isAdmin || !s.adminOnly)
    .map((s, index) => ({ ...s, number: index + 1 }));

  const normalizedQuery = query.trim().toLowerCase();
  const filteredSections = normalizedQuery
    ? visibleSections.filter((s) =>
        `${s.title} ${s.keywords || ''}`.toLowerCase().includes(normalizedQuery)
      )
    : visibleSections;

  const filteredIds = filteredSections.map((s) => s.id).join(',');

  /** 스크롤 위치에 따라 현재 보고 있는 섹션을 목차에서 강조 */
  useEffect(() => {
    const ids = filteredIds ? filteredIds.split(',') : [];
    if (ids.length === 0) {
      setActiveId(null);
      return undefined;
    }

    let ticking = false;

    const updateActive = () => {
      ticking = false;
      const OFFSET = 120;
      let currentId = ids[0];
      for (const id of ids) {
        const el = document.getElementById(`guide-section-${id}`);
        if (!el) continue;
        if (el.getBoundingClientRect().top - OFFSET <= 0) {
          currentId = id;
        } else {
          break;
        }
      }
      // 페이지 맨 아래에서는 마지막 섹션을 활성으로 둔다
      const scrollBottom = window.scrollY + window.innerHeight;
      if (scrollBottom >= document.documentElement.scrollHeight - 4) {
        currentId = ids[ids.length - 1];
      }
      setActiveId(currentId);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActive);
    };

    updateActive();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [filteredIds]);

  /** 활성 항목이 목차 스크롤 영역 밖으로 나가면 목차 안에서만 스크롤 */
  useEffect(() => {
    const toc = tocRef.current;
    if (!toc || !activeId) return;
    const link = toc.querySelector(`[data-toc-id="${activeId}"]`);
    if (!link) return;
    const linkTop = link.offsetTop;
    const linkBottom = linkTop + link.offsetHeight;
    if (linkTop < toc.scrollTop) {
      toc.scrollTop = linkTop - 8;
    } else if (linkBottom > toc.scrollTop + toc.clientHeight) {
      toc.scrollTop = linkBottom - toc.clientHeight + 8;
    }
  }, [activeId]);

  const handleMoveTo = (id) => {
    setActiveId(id);
    const el = document.getElementById(`guide-section-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="guide-container">
      <div className="section-card guide-hero">
        <div className="guide-hero-text">
          <h1 className="section-title">이용 가이드</h1>
          <p className="section-subtitle">
            정산 관리 시스템의 메뉴별 사용법과 계산 규칙을 정리한 문서입니다.
            처음 사용한다면 위에서부터 순서대로 읽어보세요.
          </p>
          <div className="guide-hero-meta">
            <span className="guide-hero-meta-label">현재 로그인</span>
            <RoleBadge role={currentUser?.role} />
            <span className="guide-hero-user">{currentUser?.company_name || currentUser?.username || '-'}</span>
            {!isAdmin && (
              <span className="guide-hero-note">권한이 있는 메뉴의 가이드만 표시됩니다.</span>
            )}
          </div>
        </div>
        <div className="guide-search-wrap">
          <span className="guide-search-icon">🔍</span>
          <input
            type="text"
            className="guide-search"
            placeholder="찾고 싶은 내용을 입력하세요 (예: 요율, 유보금, 미정산)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" className="guide-search-clear" onClick={() => setQuery('')}>
              ×
            </button>
          )}
        </div>
      </div>

      <div className="guide-layout">
        <nav className="guide-toc" ref={tocRef}>
          <div className="guide-toc-title">목차</div>
          <ul className="guide-toc-list">
            {filteredSections.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  data-toc-id={s.id}
                  className={`guide-toc-link ${activeId === s.id ? 'active' : ''}`}
                  onClick={() => handleMoveTo(s.id)}
                >
                  <span className="guide-toc-link-text">{s.number}. {s.title}</span>
                  {s.adminOnly && <span className="guide-toc-tag">본사</span>}
                </button>
              </li>
            ))}
            {filteredSections.length === 0 && (
              <li className="guide-toc-empty">검색 결과가 없습니다.</li>
            )}
          </ul>
        </nav>

        <div className="guide-content">
          {filteredSections.length === 0 ? (
            <div className="section-card">
              <div className="no-data">
                「{query}」에 해당하는 내용을 찾지 못했습니다. 다른 단어로 검색해보세요.
              </div>
            </div>
          ) : (
            filteredSections.map((s) => (
              <section
                key={s.id}
                id={`guide-section-${s.id}`}
                className="section-card guide-section"
              >
                <div className="guide-section-header">
                  <h2 className="guide-section-title">{s.number}. {s.title}</h2>
                  {s.adminOnly && <span className="guide-admin-tag">본사 전용</span>}
                </div>
                <div className="guide-section-body">{s.body}</div>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default UserGuide;
